import type { AppInfoPage } from "@/types";

/** The modal ids, so the toolbar and the overlays agree on which is which. */
export const APP_INFO_MODAL_ID = "app-info";
export const TUTORIAL_ID = "tutorial";

/**
 * Where "this browser has seen this project before" is recorded.
 *
 * Per project rather than per origin: one deployment serves every tenant, and a
 * visitor who has seen one project's introduction has not seen another's.
 */
export function visitedKey(domain: string): string {
  return `${domain}-mpr-visited`;
}

/**
 * Whether the introduction shows on this load.
 *
 * `onEveryLoad` means what it says; `onFirstLoad` only when this browser has not
 * seen the project. A project with the modal enabled but no pages authored in the
 * current language shows nothing rather than an empty dialog.
 */
export function showsAppInfo({
  enabled,
  emergence,
  pageCount,
  isFirstVisit,
}: {
  enabled: boolean | undefined;
  emergence: string | undefined;
  pageCount: number;
  isFirstVisit: boolean;
}): boolean {
  if (!enabled || pageCount === 0) return false;

  return emergence === "onEveryLoad" || (isFirstVisit && emergence === "onFirstLoad");
}

/**
 * Whether the tutorial overlay shows on this load.
 *
 * Only on a first visit, and only when the introduction isn't taking that slot:
 * two full-screen overlays on the same first paint would stack.
 */
export function showsTutorialOnLoad({
  appInfoEnabled,
  tutorialOnFirstLoad,
  isFirstVisit,
}: {
  appInfoEnabled: boolean | undefined;
  tutorialOnFirstLoad: boolean | undefined;
  isFirstVisit: boolean;
}): boolean {
  return isFirstVisit && !appInfoEnabled && !!tutorialOnFirstLoad;
}

/** The pages authored for `locale`, or none. */
export function pagesFor(
  pages: Record<string, AppInfoPage[]> | undefined,
  locale: string
): AppInfoPage[] {
  return pages?.[locale] ?? [];
}

/**
 * The CMS URL of a page's illustration.
 *
 * These assets are not mirrored by the sync — unlike POI media and the config's
 * own images — so they are still addressed on the CMS directly, as legacy did.
 * The host is a setting rather than a literal so a different environment can
 * point elsewhere. Mirroring them is a backend change worth making; the
 * descriptions embed absolute CMS URLs of their own, so it would not remove the
 * dependency on its own.
 *
 * The field is a Squidex asset *list*, which arrives as an array of one.
 */
export function appInfoImageUrl(
  image: AppInfoPage["image"],
  domain: string | undefined,
  cmsUrl: string | undefined
): string | null {
  const id = Array.isArray(image) ? image[0] : image;
  if (!id || !domain || !cmsUrl) return null;

  return `${cmsUrl.replace(/\/$/, "")}/api/assets/${encodeURIComponent(domain)}/${encodeURIComponent(id)}`;
}
